import { Node } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { processRawFragmentValue, processRawValue, processRawValueWithoutValidation } from '../../utils/processRawValue';
import { editorCommandToPMCommand } from '../editor-commands';
import { createThrottleSchedule, returnDocumentRequest } from './requestDocument';

/**
 * Core plugin that is always included in the preset.
 * Allows for executing `EditorCommand` and other core functionality.
 */
export var corePlugin = function corePlugin(_ref) {
  var config = _ref.config;
  // Create the document request throttler per editor (rather than at a module level)
  var scheduleDocumentRequest = createThrottleSchedule(returnDocumentRequest);
  return {
    name: 'core',
    actions: {
      execute: function execute(command) {
        var editorView = config === null || config === void 0 ? void 0 : config.getEditorView();
        if (!editorView || !command) {
          return false;
        }
        var state = editorView.state,
          dispatch = editorView.dispatch;
        return editorCommandToPMCommand(command)(state, dispatch);
      },
      // Code copied from `EditorActions.focus()`
      focus: function focus(options) {
        var _options$scrollIntoVi;
        var editorView = config === null || config === void 0 ? void 0 : config.getEditorView();
        if (!editorView || editorView.hasFocus()) {
          return false;
        }
        editorView.focus();
        if ((_options$scrollIntoVi = options === null || options === void 0 ? void 0 : options.scrollIntoView) !== null && _options$scrollIntoVi !== void 0 ? _options$scrollIntoVi : true) {
          editorView.dispatch(editorView.state.tr.scrollIntoView());
        }
        return true;
      },
      // Code copied from `EditorActions.blur()`
      blur: function blur() {
        var editorView = config === null || config === void 0 ? void 0 : config.getEditorView();
        if (!editorView || !editorView.hasFocus()) {
          return false;
        }

        // Ignored via go/ees005
        // eslint-disable-next-line @atlaskit/editor/no-as-casting
        editorView.dom.blur();
        return true;
      },
      scrollToPos: function scrollToPos(pos, scrollOptions) {
        var editorView = config === null || config === void 0 ? void 0 : config.getEditorView();
        if (!editorView) {
          return false;
        }
        var tr = editorView.state.tr;
        var isValidPos = typeof pos === 'number' && pos >= 0 && pos <= tr.doc.content.size;
        if (!isValidPos) {
          return false;
        }
        var dom = editorView.domAtPos(pos).node;
        if (!(dom instanceof Element)) {
          // If it's not an element (e.g. #text node), we'll try the parent.
          // This is expected to cover most of the scenarios, if not, window.scrollTo is an alternative
          if (dom.parentNode instanceof Element) {
            dom.parentNode.scrollIntoView(scrollOptions);
            return true;
          }
          return false;
        }
        dom.scrollIntoView(scrollOptions);
        return true;
      },
      replaceDocument: function replaceDocument(replaceValue, options) {
        var editorView = config === null || config === void 0 ? void 0 : config.getEditorView();
        if (!editorView || replaceValue === undefined || replaceValue === null) {
          return false;
        }
        var state = editorView.state;
        var schema = state.schema;
        var content = options !== null && options !== void 0 && options.skipValidation ? processRawValueWithoutValidation(schema, replaceValue) : Array.isArray(replaceValue) ? processRawFragmentValue(schema, replaceValue) : processRawValue(schema, replaceValue);

        // Don't replace the document if it's the same document, as full size
        // replace transactions cause issues for collaborative editing and
        // content reconciliation (eg. inline comments getting dropped)
        if (
        // eslint-disable-next-line @atlaskit/platform/no-preconditioning
        fg('platform_editor_replace_document_shortcircuit') && content instanceof Node && state.doc.eq(content)) {
          return false;
        }
        if (content) {
          var _options$scrollIntoVi2;
          var tr = state.tr.replaceWith(0, state.doc.nodeSize - 2, content);
          if ((_options$scrollIntoVi2 = options === null || options === void 0 ? void 0 : options.scrollIntoView) !== null && _options$scrollIntoVi2 !== void 0 ? _options$scrollIntoVi2 : true) {
            editorView.dispatch(tr.scrollIntoView());
          } else {
            editorView.dispatch(tr);
          }
          return true;
        }
        return false;
      },
      // Ignored via go/ees005
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      requestDocument: function requestDocument(onReceive, options) {
        var _config$getEditorView;
        var view = (_config$getEditorView = config === null || config === void 0 ? void 0 : config.getEditorView()) !== null && _config$getEditorView !== void 0 ? _config$getEditorView : null;
        scheduleDocumentRequest(view, onReceive, options === null || options === void 0 ? void 0 : options.transformer, config === null || config === void 0 ? void 0 : config.fireAnalyticsEvent);
      },
      createTransformer: function createTransformer(cb) {
        var _config$getEditorView2;
        var view = (_config$getEditorView2 = config === null || config === void 0 ? void 0 : config.getEditorView()) !== null && _config$getEditorView2 !== void 0 ? _config$getEditorView2 : null;
        if (!(view !== null && view !== void 0 && view.state.schema)) {
          return undefined;
        }
        return cb(view === null || view === void 0 ? void 0 : view.state.schema);
      }
    }
  };
};